import TrackReturnModal from '@c/modals/TrackReturnModal';
import Button from '@ui/Button';
import { calculateEarnings } from '@util/firestore/orders';
import { OrderDocument, OrderItemDocument } from 'models/order';
import { ReturnDocument, ReturnState } from 'models/returns';
import { useState } from 'react';
import { MarkReturnAsReceivedModal } from './MarkReturnAsReceivedModal';
import { useProduct } from '@util/hooks/useProduct';

export default function ReturnActions({
  ret,
  variant,
  item,
  order,
  isDetailPage,
}: {
  ret: ReturnDocument;
  variant: 'buyer' | 'seller' | 'warehouse';
  item?: OrderItemDocument;
  order?: OrderDocument | null;
  isDetailPage?: boolean;
}) {
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [showMarkAsReceivedModal, setShowMarkAsReceivedModal] = useState(false);
  const {
    data: { data: product },
  } = useProduct(ret.relisted_product_id ?? '');
  const [_, commission] =
    item && order
      ? calculateEarnings({
          item,
          item_count: item.qty,
          created: order.created,
        })
      : [0, 0];
  const purchase_price = ret.final_amount
    ? Number((ret.final_amount - commission).toFixed(2))
    : ret.eligible_refund_amount
    ? Number(ret.eligible_refund_amount.toFixed(2))
    : undefined;
  const actions: {
    label: string;
    type: 'outline' | 'secondary';
    href?: string;
    onClick?: () => void;
    index: number;
  }[] = [];
  if (!isDetailPage) {
    actions.push({
      label: 'View Return Details',
      type: 'outline',
      href: `/dashboard/${variant === 'buyer' ? 'returns' : 'seller-returns'}/${
        ret.id
      }`,
      index: 2,
    });
  }
  if (variant === 'buyer') {
    actions.push({
      label: 'Help with Return',
      type: 'outline',
      href: `/dashboard/messages/${ret.chat_id}`,
      index: 3,
    });
  }
  if (
    ret.tracking_number &&
    ret.state >= ReturnState.InTransit &&
    ret.state < ReturnState.ReturnComplete &&
    !ret.received_by_warehouse
  ) {
    actions.push({
      label: 'Track Return',
      type: 'secondary',
      onClick: () => {
        setShowTrackingModal(true);
      },
      index: 0,
    });
  }
  if (
    ret.label_pdf &&
    ret.state === ReturnState.AwaitingDropoff &&
    variant === 'buyer'
  ) {
    actions.push({
      label: 'Print Return Label',
      type: 'secondary',
      href: ret.label_pdf,
      index: 1,
    });
  }
  if (variant === 'warehouse' || variant === 'seller') {
    if (
      !ret.relisted_product_id &&
      ret.state >= ReturnState.ReturnDelivered &&
      variant === 'warehouse'
    ) {
      actions.push({
        label: 'Re-List Item',
        type: 'secondary',
        onClick: () => {
          window.open(
            `/create-listing?id=${ret.product_id}&step=3&purchase_price=${
              purchase_price ?? 0
            }&return_id=${ret.id}`,
            '_blank'
          );
        },
        index: 0,
      });
    }
    if (ret.relisted_product_id && product) {
      actions.push({
        label: 'View Re-Listed Item',
        type: 'secondary',
        href: `/product/${product?.slug}`,
        index: 1,
      });
    }
    if (
      !ret.received_by_warehouse &&
      ret.state >= ReturnState.ReturnDelivered &&
      ret.state < ReturnState.OnHold
    ) {
      actions.push({
        label: 'Mark as Received',
        type: 'secondary',
        onClick: () => {
          setShowMarkAsReceivedModal(true);
        },
        index: 0,
      });
    }
  }
  return (
    <div className="mt-2 flex flex-col gap-2 sm:mt-0">
      {actions
        .sort((a, b) => a.index - b.index)
        .map((action, i) => (
          <Button
            key={action.label}
            type={action.type as any}
            text={action.label}
            height="small"
            width="small"
            className="text-[1.6rem]"
            href={action.href}
            onClick={action.onClick}
          />
        ))}
      <TrackReturnModal
        isOpen={showTrackingModal}
        dismiss={() => setShowTrackingModal(false)}
        returnDoc={ret}
      />
      <MarkReturnAsReceivedModal
        isOpen={showMarkAsReceivedModal}
        dismiss={() => setShowMarkAsReceivedModal(false)}
        returnDoc={ret}
      />
    </div>
  );
}
